import React, { useState, Fragment, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Paper,
  LinearProgress,
  TextField
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useStyles } from "./UpdateVersionModalStyles";
import useUpdateVersion from "./useUpdateVersion";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";


const UpdateVersionModal = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const {
    currentStep,
    error,
    githubToken,
    handleCheckUpdates,
    handleClose,
    handlePerformUpdate,
    handleUpdateGithubToken,
    loading,
    pollIntervalRef,
    pollUpdateStatus,
    setCurrentStep,
    setError,
    setGithubToken,
    setLoading,
    setShowTokenModal,
    setTokenError,
    setTokenLoading,
    setUpdateProgress,
    setUpdateStatus,
    setUpdating,
    setVersionInfo,
    showTokenModal,
    tokenError,
    tokenLoading,
    updateProgress,
    updateStatus,
    updating,
    versionInfo,
  } = useUpdateVersion({ open, onClose });

  const renderContent = () => {
    if (loading) {
      return (
        <Box className={classes.progressContainer}>
          <CircularProgress />
          <Typography className={classes.statusText}>
            {i18n.t("updateVersion.status.checking")}
          </Typography>
        </Box>
      );
    }

    if (updating) {
      return (
        <Box className={classes.progressContainer}>
          <CircularProgress />
          <Typography className={classes.statusText}>
            Actualización completa en progreso...
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={updateProgress} 
            className={classes.progressBar}
          />
          <Typography variant="body2" className={classes.statusText}>
            {updateProgress}% completado
          </Typography>
          {currentStep && (
            <Typography variant="body2" className={classes.statusText} style={{ fontStyle: 'italic', marginTop: 8 }}>
              {currentStep}
            </Typography>
          )}
          {!currentStep && (
            <Typography variant="body2" className={classes.statusText}>
              Este proceso puede tomar varios minutos...
            </Typography>
          )}
          {updateStatus?.steps && updateStatus.steps.length > 0 && (
            <Box style={{ marginTop: 16, textAlign: 'left', maxHeight: 200, overflowY: 'auto' }}>
              {updateStatus.steps.map((step, index) => (
                <Typography key={index} variant="body2" style={{ fontSize: '0.85rem', marginBottom: 4, color: theme.palette.text.secondary }}>
                  {step}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    if (error) {
      // ✅ MEJORAR MENSAJE DE ERROR: Extraer información detallada del error
      let errorMessage = error;
      let errorDetails = null;
      let errorRecommendation = null;
      
      // Si el error es un objeto con información detallada
      if (typeof error === 'object' && error !== null) {
        errorMessage = error.error || error.message || "Error durante la actualización";
        errorDetails = error.details || error.errorDetails || null;
        errorRecommendation = error.recommendation || error.errorRecommendation || null;
      }
      
      return (
        <Paper className={`${classes.alert} ${classes.errorAlert}`} style={{ backgroundColor: theme.mode === 'dark' ? '#3a1a1a' : theme.palette.error.light, color: theme.palette.text.primary }}>
          <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
            <span role="img" aria-label="Error">❌</span> Error
          </Typography>
          <Typography variant="body1" style={{ color: theme.palette.text.primary, marginBottom: errorDetails || errorRecommendation ? 8 : 0 }}>
            {errorMessage}
          </Typography>
          {errorDetails && (
            <Typography variant="body2" style={{ color: theme.palette.text.primary, marginTop: 8, marginBottom: errorRecommendation ? 8 : 0, opacity: 0.9 }}>
              <strong>Detalles:</strong> {errorDetails}
            </Typography>
          )}
          {errorRecommendation && (
            <Typography variant="body2" style={{ color: theme.palette.text.primary, marginTop: 8, fontStyle: 'italic', opacity: 0.8 }}>
              <strong>Recomendación:</strong> {errorRecommendation}
            </Typography>
          )}
        </Paper>
      );
    }

    if (updateStatus?.updateCompleted) {
      return (
        <Paper className={`${classes.alert} ${classes.successAlert}`} style={{ backgroundColor: theme.mode === 'dark' ? '#1f3a1f' : theme.palette.success.light, color: theme.palette.text.primary }}>
          <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
            <span role="img" aria-label="success">✅</span> Actualización Completada
          </Typography>
          <Typography variant="body2" paragraph style={{ color: theme.palette.text.primary }}>
            <strong>Nueva versión:</strong> {updateStatus.newVersion}
          </Typography>
          <Typography variant="body2" paragraph style={{ color: theme.palette.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            <strong>Mensaje:</strong> {updateStatus.newMessage}
          </Typography>
          <Typography variant="body2" paragraph style={{ color: theme.palette.text.primary }}>
            <strong>Autor:</strong> {updateStatus.newAuthor}
          </Typography>
          {updateStatus.steps && updateStatus.steps.length > 0 && (
            <Box className={classes.stepsList}>
              <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
                Pasos completados:
              </Typography>
              {updateStatus.steps.map((step, index) => (
                <Box key={index} className={classes.stepItem}>
                  <span role="img" aria-label="check" className={classes.stepIcon}>✅</span>
                  <Typography variant="body2" style={{ color: theme.palette.text.primary }}>{step}</Typography>
                </Box>
              ))}
            </Box>
          )}
          <Typography variant="body2" style={{ color: theme.palette.primary.main, fontWeight: "bold" }}>
            <span role="img" aria-label="check">✅</span> Actualización completada exitosamente. Los servicios se han reiniciado automáticamente.
          </Typography>
          <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, marginTop: "8px", fontSize: "0.875rem" }}>
            <span role="img" aria-label="idea">💡</span> Los usuarios experimentaron una breve reconexión automática (2-5 segundos). 
            Presiona "CERRAR" para recargar esta página.
          </Typography>
        </Paper>
      );
    }

    if (updateStatus) {
      return (
        <Box>
          {/* Información de Versiones Semánticas */}
          {versionInfo && versionInfo.semanticVersion && (
            <Paper style={{ 
              padding: "16px", 
              marginBottom: "20px", 
              backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa', 
              border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}` 
            }}>
              <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main }}>
                <span role="img" aria-label="version">📋</span> INFORMACIÓN DE VERSIONES
              </Typography>
              
              <Box style={{ marginBottom: "12px" }}>
                <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                  ├── Versión Actual: <strong>{versionInfo.semanticVersion.currentVersion}</strong>
                </Typography>
                <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                  ├── Sistema de Versiones: {versionInfo.semanticVersion.hasVersionSystem ? <><span role="img" aria-label="check mark">✅</span> Activo</> : <><span role="img" aria-label="cross mark">❌</span> Inactivo</>}
                </Typography>
                <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                  ├── Última Actualización: {versionInfo.semanticVersion.lastVersionUpdate}
                </Typography>
                <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                  └── Tags Git: {versionInfo.semanticVersion.gitTags.length} disponibles
                </Typography>
              </Box>
              
              {/* Historial de Versiones */}
              {versionInfo.semanticVersion.versionHistory && versionInfo.semanticVersion.versionHistory.length > 0 && (
                <Box style={{ marginTop: "12px" }}>
                  <Typography variant="subtitle2" gutterBottom style={{ color: theme.palette.text.secondary || theme.palette.text.primary }}>
                    <span role="img" aria-label="bar chart">📊</span> Historial de Versiones:
                  </Typography>
                  <Box style={{ maxHeight: "120px", overflowY: "auto", backgroundColor: theme.palette.background.paper, padding: "8px", borderRadius: "4px", border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}` }}>
                    {versionInfo.semanticVersion.versionHistory.slice(0, 5).map((version, index) => (
                      <Typography key={index} variant="body2" style={{ fontFamily: "monospace", fontSize: "0.75rem", color: theme.palette.text.secondary || theme.palette.text.primary }}>
                        • v{version.version} - {version.date}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          )}
          
          <Paper style={{ padding: "16px", marginBottom: "20px", backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
              Estado de Actualizaciones
            </Typography>
            <Typography variant="body1" paragraph style={{ color: theme.palette.text.primary }}>
              <strong>{i18n.t("updateVersion.messages.currentVersion")}</strong> {updateStatus.currentVersion}
            </Typography>
            {updateStatus.hasUpdates ? (
              <Typography variant="body1" paragraph style={{ color: theme.palette.text.primary }}>
                <strong>Última versión disponible:</strong> {updateStatus.latestMessage || updateStatus.latestCommitMessage || updateStatus.latestVersion}
              </Typography>
            ) : (
              <Typography variant="body1" paragraph style={{ color: theme.palette.text.primary }}>
                <strong>Descripción de la versión actual:</strong> {updateStatus.currentMessage || updateStatus.currentCommitMessage || "Sin descripción disponible"}
              </Typography>
            )}
            <Typography variant="body1" paragraph style={{ color: theme.palette.text.primary }}>
              <strong>Estado:</strong>{" "}
              {updateStatus.hasUpdates ? (
                <span style={{ color: theme.palette.success.main, fontWeight: "bold" }}>
                  <span role="img" aria-label="check">✅</span> {i18n.t("updateVersion.status.updatesAvailable")}
                </span>
              ) : (
                <span style={{ color: theme.palette.info.main, fontWeight: "bold" }}>
                  <span role="img" aria-label="check">✅</span> {i18n.t("updateVersion.status.upToDate")}
                </span>
              )}
            </Typography>
          </Paper>
          
          {updateStatus.hasUpdates && (
            <Paper className={`${classes.alert} ${classes.infoAlert}`} style={{ backgroundColor: theme.mode === 'dark' ? '#1a1f3a' : theme.palette.info.light, color: theme.palette.text.primary }}>
              <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                Hay {updateStatus.commitsAhead} nuevos commits disponibles.
              </Typography>
              
              {/* Información detallada del commit si está disponible */}
              {updateStatus.commitAnalysis && (
                <Box style={{ marginTop: "16px" }}>
                  <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main }}>
                    <span role="img" aria-label="clipboard">📋</span> DETALLES DEL COMMIT DISPONIBLE:
                  </Typography>
                  <Box style={{ 
                    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa', 
                    padding: "12px", 
                    borderRadius: "8px",
                    marginBottom: "16px",
                    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`
                  }}>
                    <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                      ├── Commit: {updateStatus.latestVersion}
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                      ├── Mensaje: "{updateStatus.latestMessage}"
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                      ├── Archivos modificados: {updateStatus.commitAnalysis.files.length} archivo(s)
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                      ├── Tipo de cambio: {
                        updateStatus.commitAnalysis.isConfigOnly ? "Configuración Git" :
                        updateStatus.commitAnalysis.isDocumentationOnly ? "Documentación" :
                        updateStatus.commitAnalysis.isCodeChange ? "Código fuente" :
                        updateStatus.commitAnalysis.requiresDatabase ? "Base de datos" :
                        "Mixto"
                      }
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                      └── Impacto: {
                        updateStatus.commitAnalysis.impact === 'high' ? "Alto" :
                        updateStatus.commitAnalysis.impact === 'medium' ? "Medio" :
                        "Bajo"
                      } ({updateStatus.commitAnalysis.estimatedTime})
                    </Typography>
                  </Box>
                  
                </Box>
              )}
              
              {/* Información sobre reinicio de servicios */}
              <Paper style={{ 
                padding: "12px", 
                backgroundColor: theme.mode === 'dark' ? '#3a2a1a' : '#fff3e0', 
                border: `1px solid ${theme.mode === 'dark' ? '#ff9800' : '#ffb74d'}`,
                marginTop: "16px",
                marginBottom: "16px"
              }}>
                <Typography variant="body2" style={{ color: theme.mode === 'dark' ? '#ffb74d' : '#e65100', fontWeight: "bold" }}>
                  <span role="img" aria-label="warning">⚠️</span> Importante: Reinicio de Servicios
                </Typography>
                <Typography variant="body2" style={{ color: theme.mode === 'dark' ? '#ffcc80' : '#bf360c', fontSize: "0.875rem", marginTop: "4px" }}>
                  • La actualización reiniciará los servicios automáticamente<br/>
                  • Los usuarios experimentarán una breve reconexión (2-5 segundos)<br/>
                  • <span role="img" aria-label="idea">💡</span> Recomendado fuera de horas pico para minimizar impacto
                </Typography>
              </Paper>
              
              {/* En Docker el contenedor no puede reemplazarse a sí mismo: no hay
                  git ni PM2 dentro. Se muestra el comando en vez de un botón que
                  fallaría. La comparación es estricta con `false`: si el backend
                  no informa el campo (versión anterior), el botón se conserva. */}
              {versionInfo?.canSelfUpdate === false ? (
                <Paper
                  className={classes.alert}
                  style={{
                    marginTop: "16px",
                    backgroundColor: theme.mode === 'dark' ? '#1e3a5f' : '#e3f2fd',
                    color: theme.palette.text.primary,
                  }}
                >
                  <Typography variant="body2" gutterBottom style={{ color: theme.palette.text.primary }}>
                    <span role="img" aria-label="docker">🐳</span> <strong>Instalación en Docker</strong>
                  </Typography>
                  <Typography variant="body2" style={{ color: theme.palette.text.primary, marginBottom: "8px" }}>
                    La actualización se ejecuta desde el servidor. Conéctate por SSH y ejecuta:
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      fontFamily: "monospace",
                      padding: "8px 12px",
                      borderRadius: 4,
                      backgroundColor: theme.mode === 'dark' ? '#111827' : '#f5f5f5',
                      color: theme.palette.text.primary,
                    }}
                  >
                    watoolx.sh update
                  </Typography>
                  <Typography variant="caption" style={{ color: theme.palette.text.secondary, display: "block", marginTop: "8px" }}>
                    El comando respalda la base de datos antes de migrar.
                  </Typography>
                </Paper>
              ) : (
                <Box style={{ display: "flex", justifyContent: "center", marginTop: "16px" }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handlePerformUpdate()}
                    className={classes.updateButton}
                    size="large"
                    style={{ minWidth: 250 }}
                  >
                    <span role="img" aria-label="update">⚡</span> Actualizar Sistema
                  </Button>
                </Box>
              )}
              

            </Paper>
          )}
          
          {/* ✅ DB-FIRST: Información de última actualización desde GitHub */}
          {!updateStatus.hasUpdates && updateStatus.currentDateTime && (
            <Paper className={classes.alert} style={{ 
              marginTop: "16px",
              backgroundColor: theme.mode === 'dark' ? '#1e3a5f' : '#e3f2fd',
              color: theme.palette.text.primary,
            }}>
              <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
                <span role="img" aria-label="clock">🕐</span> Información de Última Actualización
              </Typography>
              <Typography variant="body2" paragraph style={{ color: theme.palette.text.primary }}>
                <strong>Última actualización desde GitHub:</strong>
              </Typography>
              <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary, marginBottom: "8px" }}>
                <span role="img" aria-label="calendar">📅</span> Fecha: {updateStatus.currentDate || 'N/A'}
              </Typography>
              <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary, marginBottom: "8px" }}>
                <span role="img" aria-label="clock">🕐</span> Hora: {updateStatus.currentDateTime ? new Date(updateStatus.currentDateTime).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/Lima' }) : 'N/A'}
              </Typography>
              <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary, marginBottom: "8px" }}>
                <span role="img" aria-label="user">👤</span> Autor: {updateStatus.currentAuthor || 'N/A'}
              </Typography>
              <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                <span role="img" aria-label="bookmark">🔖</span> Commit: {updateStatus.currentVersion || 'N/A'}
              </Typography>
            </Paper>
          )}
        </Box>
      );
    }

    return (
      <Box style={{ textAlign: 'center', padding: '20px 0', backgroundColor: theme.palette.background.paper }}>
        <Box style={{ marginBottom: 24 }}>
          <span style={{ fontSize: '48px' }} role="img" aria-label="tools">🔧</span>
        </Box>
        
        <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
          Mantenimiento del Sistema
        </Typography>
        
        <Typography variant="body1" paragraph style={{ color: theme.palette.text.secondary || theme.palette.text.primary, lineHeight: 1.6 }}>
          Verifica si hay actualizaciones disponibles para mantener tu sistema Watoolx 
          funcionando de manera óptima y segura.
        </Typography>
        
        <Box style={{ 
          background: theme.mode === 'dark' ? '#374151' : '#e3f2fd', 
          padding: 16, 
          borderRadius: 8, 
          margin: '16px 0',
          border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#2196f3'}`
        }}>
          <Typography variant="body2" style={{ color: theme.mode === 'dark' ? '#60a5fa' : '#1976d2', fontWeight: 'bold' }}>
            <span role="img" aria-label="lightbulb">💡</span> ¿Qué incluyen las actualizaciones?
          </Typography>
          <Box component="ul" style={{ margin: '8px 0', paddingLeft: 20 }}>
            <Typography component="li" variant="body2" style={{ color: theme.mode === 'dark' ? '#93c5fd' : '#1976d2' }}>
              Mejoras de seguridad y estabilidad
            </Typography>
            <Typography component="li" variant="body2" style={{ color: theme.mode === 'dark' ? '#93c5fd' : '#1976d2' }}>
              Nuevas funcionalidades y correcciones
            </Typography>
            <Typography component="li" variant="body2" style={{ color: theme.mode === 'dark' ? '#93c5fd' : '#1976d2' }}>
              Optimizaciones de rendimiento
            </Typography>
          </Box>
        </Box>
        
        <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, fontStyle: 'italic' }}>
          Haz clic en "Verificar Actualizaciones" para comenzar
        </Typography>
      </Box>
    );
  };

  return (
    <Fragment>
    <Dialog 
      open={open} 
      onClose={handleClose} 
      className={classes.root}
      maxWidth="sm"
      fullWidth={false}
    >
      <DialogTitle style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <span style={{ fontSize: '24px' }} role="img" aria-label="rocket">🚀</span>
        <Typography variant="h5" style={{ fontWeight: 'bold', color: 'white' }}>
          Actualización del Sistema
        </Typography>
      </DialogTitle>
      
      <DialogContent className={classes.content} style={{ padding: '16px 20px', backgroundColor: theme.palette.background.paper }}>
        {renderContent()}
      </DialogContent>
      
      <DialogActions style={{ 
        padding: '12px 20px',
        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#eee'}`,
        background: theme.mode === 'dark' ? '#1f2937' : '#f8f9fa'
      }}>
        <Button 
          onClick={handleClose} 
          color="secondary" 
          disabled={updating}
          variant="outlined"
          style={{ marginRight: 8 }}
        >
          {updating ? 'Procesando...' : 'Cerrar'}
        </Button>
        {!loading && !updateStatus && !updating && (
          <Button 
            onClick={handleCheckUpdates} 
            color="primary" 
            variant="contained"
            startIcon={<span role="img" aria-label="search">🔍</span>}
          >
            Verificar Actualizaciones
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* ✅ FASE 4: Modal para ingresar token de GitHub - FUERA del Dialog principal para que se muestre independientemente */}
    <Dialog
      open={showTokenModal}
      onClose={() => {
        setShowTokenModal(false);
        setGithubToken("");
        setTokenError(null);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle style={{
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: 'white',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <span style={{ fontSize: '24px' }} role="img" aria-label="key">🔑</span>
        <Typography variant="h6" style={{ fontWeight: 'bold', color: 'white' }}>
          Actualizar Token de GitHub
        </Typography>
      </DialogTitle>
      
      <DialogContent style={{ padding: '24px', backgroundColor: theme.palette.background.paper }}>
        <Typography variant="body1" paragraph style={{ color: theme.palette.text.primary, marginBottom: 16 }}>
          El token de GitHub Personal Access Token (PAT) ha expirado o es inválido. Por favor, ingresa un nuevo token para continuar.
        </Typography>
        
        {error && (
          <Paper style={{ 
            padding: '12px', 
            marginBottom: 16, 
            backgroundColor: theme.mode === 'dark' ? '#3a1a1a' : '#ffebee',
            border: `1px solid ${theme.palette.error.main}`
          }}>
            <Typography variant="body2" style={{ color: theme.palette.error.main }}>
              <strong>Error:</strong> {error.error}
            </Typography>
            {error.details && (
              <Typography variant="body2" style={{ color: theme.palette.error.main, marginTop: 8, fontSize: '0.875rem' }}>
                {error.details}
              </Typography>
            )}
          </Paper>
        )}

        <TextField
          fullWidth
          label="Token de GitHub (PAT)"
          type="password"
          value={githubToken}
          onChange={(e) => {
            setGithubToken(e.target.value);
            setTokenError(null);
          }}
          placeholder="github_pat_..."
          variant="outlined"
          error={!!tokenError}
          helperText={tokenError || "Ingresa tu Personal Access Token de GitHub"}
          disabled={tokenLoading}
          style={{ marginBottom: 16 }}
          InputProps={{
            style: { color: theme.palette.text.primary }
          }}
          InputLabelProps={{
            style: { color: theme.palette.text.secondary || theme.palette.text.primary }
          }}
        />

        <Box style={{ marginTop: 16, padding: '12px', backgroundColor: theme.mode === 'dark' ? '#374151' : '#f3f4f6', borderRadius: '8px', border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}` }}>
          <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, fontSize: '0.875rem', marginBottom: 8, fontWeight: 500 }}>
            <span role="img" aria-label="info">ℹ️</span> Información sobre el token:
          </Typography>
          <Typography variant="body2" component="div" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, fontSize: '0.875rem', lineHeight: 1.6 }}>
            • El token debe comenzar con <code style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#ffffff', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>github_pat_</code>
          </Typography>
          <Typography variant="body2" component="div" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, fontSize: '0.875rem', lineHeight: 1.6 }}>
            • Debe tener al menos 50 caracteres de longitud
          </Typography>
          <Typography variant="body2" component="div" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, fontSize: '0.875rem', lineHeight: 1.6 }}>
            • Puedes crear un nuevo token en: <code style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#ffffff', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>GitHub → Settings → Developer settings → Personal access tokens</code>
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions style={{ 
        padding: '12px 24px',
        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#eee'}`,
        background: theme.mode === 'dark' ? '#1f2937' : '#f8f9fa'
      }}>
        <Button
          onClick={() => {
            setShowTokenModal(false);
            setGithubToken("");
            setTokenError(null);
          }}
          color="secondary"
          disabled={tokenLoading}
          variant="outlined"
          style={{ marginRight: 8 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleUpdateGithubToken}
          color="primary"
          variant="contained"
          disabled={tokenLoading || !githubToken.trim()}
          startIcon={tokenLoading ? <CircularProgress size={16} /> : <span role="img" aria-label="save">💾</span>}
        >
          {tokenLoading ? "Actualizando..." : "Actualizar Token"}
        </Button>
      </DialogActions>
    </Dialog>
    </Fragment>
  );
};

export default UpdateVersionModal;
